import * as React from 'react';
import { toast } from 'sonner';
import { Link, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, ChevronDown, Copy } from 'lucide-react';
import {
  Button,
  Card,
  Menu,
  MenuContent,
  MenuGroup,
  MenuGroupLabel,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '@/shared/ui';
import { useCopyToClipboard } from '@/shared/lib/useCopyToClipboard';
import { useDocNav } from '../model/useDocNav';
import { useDocActions } from '../model/useDocActions';
import { AI_PROVIDERS } from '../model/aiProviders';
import { MarkdownContent } from './MarkdownContent';
import { DocToc } from './DocToc';
import tokens from '../tokens.module.css';
import styles from './DocViewer.module.css';

type DocViewerProps = {
  docId: string;
  title: string;
  markdown: string;
  bar?: React.ReactNode;
};

const HASH_SCROLL_MAX_ATTEMPTS = 120;

const DocViewerInner = React.memo(function DocViewerInner({
  docId,
  title,
  markdown,
  bar,
}: DocViewerProps) {
  const { prev, next } = useDocNav(docId);
  const { state } = useLocation();
  const { openInAI } = useDocActions(markdown);
  const { copied, copy } = useCopyToClipboard();

  const handleCopy = React.useCallback(() => {
    copy(markdown);
  }, [copy, markdown]);

  const handleCopyLink = React.useCallback(() => {
    const url = window.location.href.split('#')[0];
    void navigator.clipboard.writeText(url).then(
      () => toast.success('Ссылка скопирована'),
      () => toast.error('Не удалось скопировать'),
    );
  }, []);

  React.useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;
    if (new URLSearchParams(window.location.search).has('q')) return;
    const id = decodeURIComponent(hash.slice(1));
    let cancelled = false;
    let attempts = 0;
    const tick = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      if (attempts < HASH_SCROLL_MAX_ATTEMPTS) {
        attempts += 1;
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
    return () => {
      cancelled = true;
    };
  }, [docId]);

  return (
    <div className={`${tokens.docViewerScope} ${styles.root}`}>
      <div className={styles.contentColumn}>
        {bar && <div className={styles.barSlot}>{bar}</div>}
        <header className={styles.header}>
          <h1 className={styles.title}>
            <button
              type='button'
              onClick={handleCopyLink}
              className={styles.titleAnchor}
              aria-label='Скопировать ссылку на статью'
              tabIndex={-1}
            >
              #
            </button>
            {title}
          </h1>
          <div className={styles.toolbar}>
            <div className={styles.copyGroup}>
              <Button variant='secondary' size='sm' onClick={handleCopy} className={styles.copyBtn}>
                {copied ? <Check size={15} aria-hidden /> : <Copy size={15} aria-hidden />}
                Скопировать
              </Button>
              <Menu>
                <MenuTrigger render={<Button variant='secondary' size='sm' className={styles.aiBtn} />}>
                  AI
                  <ChevronDown size={15} aria-hidden />
                </MenuTrigger>
                <MenuContent className={styles.copyMenu} align='start'>
                  <MenuGroup>
                    <MenuGroupLabel>Ответит сразу</MenuGroupLabel>
                    {AI_PROVIDERS.filter((p) => !p.authRequired).map((provider) => (
                      <MenuItem key={provider.id} onClick={() => openInAI(provider)}>
                        <provider.Icon size={15} aria-hidden />
                        Скопировать и спросить {provider.label}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                  <MenuSeparator />
                  <MenuGroup>
                    <MenuGroupLabel>Нужна авторизация</MenuGroupLabel>
                    {AI_PROVIDERS.filter((p) => p.authRequired).map((provider) => (
                      <MenuItem key={provider.id} onClick={() => openInAI(provider)}>
                        <provider.Icon size={15} aria-hidden />
                        Скопировать и спросить {provider.label}
                      </MenuItem>
                    ))}
                  </MenuGroup>
                </MenuContent>
              </Menu>
            </div>
          </div>
        </header>

        <MarkdownContent markdown={markdown} />

        {(prev || next) && (
          <nav className={styles.footer} aria-label='Соседние инструкции'>
            {prev && (
              <Link to={`/instructions/${prev.urlId}`} state={state} className={styles.footerLink}>
                <Card className={styles.footerCard}>
                  <span className={styles.footerHint}>
                    <ArrowLeft size={14} aria-hidden />
                    Предыдущая
                  </span>
                  <span className={styles.footerTitle}>{prev.title}</span>
                </Card>
              </Link>
            )}
            {next && (
              <Link
                to={`/instructions/${next.urlId}`}
                state={state}
                className={`${styles.footerLink} ${styles.footerLinkNext}`}
              >
                <Card className={styles.footerCard}>
                  <span className={styles.footerHint}>
                    Следующая
                    <ArrowRight size={14} aria-hidden />
                  </span>
                  <span className={styles.footerTitle}>{next.title}</span>
                </Card>
              </Link>
            )}
          </nav>
        )}
      </div>

      <aside className={styles.tocWrapper}>
        <DocToc markdown={markdown} />
      </aside>
    </div>
  );
});

export function DocViewer(props: DocViewerProps) {
  return <DocViewerInner {...props} />;
}
